import React, { useCallback, useEffect, useState } from "react";
import CardSection from "../components/CardSection";
import {
  getSocialAccounts,
  connectSocialAccount,
  generateImage,
  generateVideo,
  schedulePost,
  postNow,
  postAll,
  getScheduledPosts,
  deleteScheduledPost,
  api,
  getTwitterAuthUrl,
  getLinkedInAuthUrl,
  abTestPosts,
} from "../api";
import InfoCard from "../components/InfoCard";
import PromoTemplateGenerator from "../components/PromoTemplateGenerator";
import { useSubscription } from "../SubscriptionContext";
import UsageBadge from "../components/UsageBadge";
import UpgradePrompt from "../components/UpgradePrompt";
import toast from "../components/Toast";

function apiErrorMessage(err) {
  const d = err.response?.data;
  if (typeof d?.error === "string") return d.error;
  if (d?.detail) return `${d.error || "Error"}: ${d.detail}`;
  return err.message || "Action failed";
}

function isLimitError(err) {
  return err.response?.status === 403 && err.response?.data?.details?.resource;
}

export default function SocialMediaTab() {
  const { subscription, refresh: refreshSubscription } = useSubscription();
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [generated, setGenerated] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [bulkPosts, setBulkPosts] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [abTests, setAbTests] = useState([]);
  const [msg, setMsg] = useState("");
  const [msgTone, setMsgTone] = useState("info");
  const [busy, setBusy] = useState({
    text: false,
    image: false,
    video: false,
    schedule: false,
    postNow: false,
    postAll: false,
    bulkSchedule: false,
  });
  const [postAllResults, setPostAllResults] = useState(null);
  // Media generation options
  const [imageSize] = useState("instagram_square");
  const [imageQuality] = useState("standard");
  const [videoFormat] = useState("landscape_16_9");
  const [videoDuration, setVideoDuration] = useState("5");
  const [videoCamera] = useState("dynamic");
  const [showPromoTemplate, setShowPromoTemplate] = useState(false);
  const [imageMode, setImageMode] = useState("promo"); // "promo" | "product"
  // Product/reference image upload
  const [referenceImage, setReferenceImage] = useState(null);   // base64
  const [referenceImageName, setReferenceImageName] = useState("");
  const [referenceImagePreview, setReferenceImagePreview] = useState("");
  const fileInputRef = React.useRef(null);

  const handleReferenceImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setBanner("Image must be under 5MB", "error"); return; }
    setReferenceImageName(file.name);
    setReferenceImagePreview(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result.split(",")[1];
      setReferenceImage(b64);
      // Auto-analyze the image with AI
      analyzeProductImage(b64, file.type || "image/jpeg");
    };
    reader.readAsDataURL(file);
  };

  const analyzeProductImage = async (b64, mimeType) => {
    setBanner("🔍 Analyzing your product image with AI...", "info");
    try {
      const { data } = await api.post("/social/analyze-image", { image_b64: b64, mime_type: mimeType });
      if (data.description) {
        setTopic(data.description);
        setBanner("✅ Product analyzed! Topic updated. Now generate text/image/video.", "success");
      }
    } catch (e) {
      setBanner("Image uploaded. Add your topic and generate.", "info");
    }
  };

  const clearReferenceImage = () => {
    setReferenceImage(null);
    setReferenceImageName("");
    setReferenceImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleBulkSchedule = async () => {
    if (bulkPosts.length === 0) return;
    setBusy((b) => ({ ...b, bulkSchedule: true }));
    try {
      await schedulePost(bulkPosts);
      setBanner(`${bulkPosts.length} posts scheduled.`, "success");
      setBulkPosts([]);
      setTimeout(fetchScheduledPosts, 400);
    } catch (err) {
      setBanner(apiErrorMessage(err), "error");
    } finally {
      setBusy((b) => ({ ...b, bulkSchedule: false }));
    }
  };

  const handleAbTest = async () => {
    const t = topic.trim();
    if (!t) {
      setBanner("Enter a topic first.", "error");
      return;
    }
    setBusy((b) => ({ ...b, text: true }));
    try {
      const { data } = await abTestPosts({ topic: t, platform, variants: 3 });
      setAbTests(data);
      setBanner("A/B test variants generated.", "success");
    } catch (err) {
      setBanner(apiErrorMessage(err), "error");
    } finally {
      setBusy((b) => ({ ...b, text: false }));
    }
  };

  const setBanner = (text, tone = "info") => {
    setMsg(text);
    setMsgTone(tone);
  };

  const fetchScheduledPosts = useCallback(() => {
    getScheduledPosts()
      .then(({ data }) => {
        const items = data.items || data;
        setScheduledPosts(items);
      })
      .catch(() => {
        setMsg("Could not load scheduled posts.");
        setMsgTone("error");
      });
  }, []);

  const loadAccounts = useCallback(() => {
    getSocialAccounts()
      .then(({ data }) => {
        const items = data.items || data;
        setAccounts(items);
        setAccountId((prev) => {
          if (!items.length) return "";
          if (prev && items.some((a) => String(a.id) === String(prev))) return prev;
          return String(items[0].id);
        });
      })
      .catch(() => setBanner("Unable to load accounts.", "error"));
  }, []);

  useEffect(() => {
    loadAccounts();
    fetchScheduledPosts();
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("social_connected");
    if (connected) {
      setBanner(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`, "success");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(loadAccounts, 500);
    }

    // Listen for OAuth success messages from popup windows
    const handleOAuthMessage = (event) => {
      // Verify origin for security
      const allowedOrigins = [window.location.origin, 'http://localhost:3000', 'https://kronos.devxhouse.com'];
      if (!allowedOrigins.some(o => event.origin.startsWith(o.split(',')[0]))) return;

      if (event.data && event.data.type === 'oauth_success') {
        const platform = event.data.platform;
        setBanner(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected successfully!`, "success");
        loadAccounts();
        fetchScheduledPosts();
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [fetchScheduledPosts, loadAccounts]);

  // OAuth popup helper - opens in new tab/window and listens for completion
  const openOAuthPopup = (url, platform) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      url,
      `${platform}_oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    // Poll for popup close and check for success
    const pollTimer = setInterval(() => {
      try {
        // Check if popup was closed
        if (!popup || popup.closed) {
          clearInterval(pollTimer);
          // Refresh accounts after OAuth completes
          setTimeout(() => {
            loadAccounts();
            fetchScheduledPosts();
          }, 1000);
        }
        // Try to read popup URL (will fail if on external domain due to CORS)
        if (popup && popup.location && popup.location.href) {
          const url = popup.location.href;
          if (url.includes('social_connected=') || url.includes('/social-media')) {
            clearInterval(pollTimer);
            popup.close();
            const params = new URLSearchParams(popup.location.search);
            const connected = params.get('social_connected');
            if (connected) {
              setBanner(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`, "success");
            }
            loadAccounts();
            fetchScheduledPosts();
          }
        }
      } catch (e) {
        // Cross-origin error is expected when popup is on OAuth provider domain
      }
    }, 500);

    return popup;
  };

  const handleFacebookConnect = async () => {
    try {
      const { data } = await api.get("/social/facebook/login");
      if (data.redirect_url) {
        openOAuthPopup(data.redirect_url, 'facebook');
      }
    } catch {
      setBanner("Could not start Facebook connection. Is FACEBOOK_APP_ID configured?", "error");
    }
  };

  const handleTwitterConnect = async () => {
    try {
      const { data } = await getTwitterAuthUrl();
      if (data.redirect_url) {
        openOAuthPopup(data.redirect_url, 'twitter');
      }
    } catch (e) {
      setBanner(apiErrorMessage(e), "error");
    }
  };

  const handleLinkedInConnect = async () => {
    try {
      const { data } = await getLinkedInAuthUrl();
      if (data.redirect_url) {
        openOAuthPopup(data.redirect_url, 'linkedin');
      }
    } catch (e) {
      setBanner(apiErrorMessage(e), "error");
    }
  };

  const handleTikTokConnect = () => {
    setBanner("TikTok integration coming soon — awaiting API approval from TikTok.", "info");
  };

  const handlePinterestConnect = () => {
    setBanner("Pinterest integration coming soon.", "info");
  };

  const handleCtaClick = async (action) => {
    if (busy.text || busy.image || busy.video || busy.schedule || busy.postAll || busy.bulkSchedule) return;
    setBanner("");
    try {
      if (action === "connect") {
        const platform = window.prompt("Platform name? (e.g. Twitter)");
        const handle = window.prompt("Your handle?");
        if (!platform || !handle) return;
        const { data } = await connectSocialAccount(platform, handle);
        setAccounts((a) => [...a, data]);
        setAccountId(String(data.id));
        setBanner("Account connected.", "success");
        return;
      }
      if (action === "generate-text") {
        const t = topic.trim();
        if (!t) {
          setBanner("Enter a topic first.", "error");
          return;
        }
        setBusy((b) => ({ ...b, text: true }));
        setBanner("Writing your dynamic, long-form caption…", "info");
        try {
          const { data } = await api.post("/social/generate-content", { topic: t.slice(0, 1000), platform });
          setGenerated(data);
          setImageUrl("");
          setBanner("✨ Caption ready — generate an image when you want.", "success");
        } catch (err) {
          setBanner(apiErrorMessage(err), "error");
        }
        return;
      }
      if (action === "generate-image") {
        const prompt =
          generated?.image_prompt?.trim() || topic.trim() || "";
        if (!prompt) {
          setBanner("Generate text first (or enter a topic).", "error");
          return;
        }
        setBusy((b) => ({ ...b, image: true }));
        const modeLabel = referenceImage ? (imageMode === "promo" ? "🎨 Creating promotional poster…" : "📸 Creating product photo…") : "Creating your image…";
        setBanner(modeLabel, "info");
        try {
          const quality = referenceImage ? imageMode : imageQuality;
          const { data } = await generateImage(prompt, { size: imageSize, quality, reference_image: referenceImage || undefined });
          if (data.error) {
            setBanner(data.detail ? `${data.error} (${data.detail})` : data.error, "error");
            return;
          }
          setImageUrl(data.image_url);
          refreshSubscription();
          setBanner(
            data.source === "openai"
              ? "Image ready (DALL·E 3)."
              : "Image ready.",
            "success"
          );
        } catch (err) {
          if (isLimitError(err)) refreshSubscription();
          setBanner(apiErrorMessage(err), "error");
        }
        return;
      }
      if (action === "schedule-post") {
        if (!generated) {
          setBanner("Generate post text before scheduling.", "error");
          return;
        }
        if (!accounts.length || !accountId) {
          setBanner("Connect a social account first (use the panel below).", "error");
          return;
        }
        if (!scheduleTime) {
          setBanner("Pick a date and time for the post.", "error");
          return;
        }
        const when = new Date(scheduleTime);
        if (Number.isNaN(when.getTime())) {
          setBanner("That date/time is not valid.", "error");
          return;
        }
        if (when.getTime() < Date.now()) {
          setBanner("Choose a time in the future.", "error");
          return;
        }
        if (bulkMode) {
          // Add to bulk
          setBulkPosts(prev => [...prev, {
            content: generated.caption,
            hashtags: generated.hashtags,
            image_url: imageUrl || undefined,
            video_url: videoUrl || undefined,
            post_time: when.toISOString(),
            account_id: Number(accountId),
          }]);
          setBanner("Added to bulk schedule.", "success");
        } else {
          // Schedule now
          setBusy((b) => ({ ...b, schedule: true }));
          await schedulePost({
            content: generated.caption,
            hashtags: generated.hashtags,
            image_url: imageUrl || undefined,
            video_url: videoUrl || undefined,
            post_time: when.toISOString(),
            account_id: Number(accountId),
          });
          setBanner("Post scheduled.", "success");
          setTimeout(fetchScheduledPosts, 400);
        }
        return;
      }
      if (action === "post-now") {
        if (!generated) {
          setBanner("Generate post text before posting.", "error");
          return;
        }
        if (!accounts.length || !accountId) {
          setBanner("Connect a social account first (use the panel below).", "error");
          return;
        }
        setBusy((b) => ({ ...b, postNow: true }));
        setBanner("Publishing your post now…", "info");
        try {
          const { data } = await postNow({
            content: generated.caption,
            hashtags: generated.hashtags,
            image_url: imageUrl || undefined,
            video_url: videoUrl || undefined,
            account_id: Number(accountId),
          });
          refreshSubscription();
          setBanner(`Posted to ${data.platform || "your account"}!`, "success");
        } catch (err) {
          if (isLimitError(err)) refreshSubscription();
          setBanner(apiErrorMessage(err), "error");
        }
        return;
      }
      if (action === "post-all") {
        if (!generated) {
          setBanner("Generate post text before posting.", "error");
          return;
        }
        setBusy((b) => ({ ...b, postAll: true }));
        setBanner("Publishing to all connected accounts…", "info");
        try {
          const { data } = await postAll({
            content: generated.caption,
            hashtags: generated.hashtags,
            image_url: imageUrl || undefined,
            video_url: videoUrl || undefined,
          });
          refreshSubscription();
          setPostAllResults(data.results);
          setBanner(data.message, "success");
        } catch (err) {
          if (isLimitError(err)) refreshSubscription();
          setBanner(apiErrorMessage(err), "error");
        }
        return;
      }
      if (action === "generate-video") {
        const prompt =
          generated?.video_prompt || generated?.image_prompt?.trim() || topic.trim() || "";
        if (!prompt) {
          setBanner("Generate text first (or enter a topic).", "error");
          return;
        }
        setBusy((b) => ({ ...b, video: true }));
        // Only pass reference image if it's small enough (< 500KB base64 = ~375KB image)
        const videoRefImage = referenceImage && referenceImage.length < 700000 ? referenceImage : undefined;
        setBanner(videoRefImage ? "🎬 Creating promotional video for your product with Seedance…" : "🎬 Creating AI video with Seedance (30-90 seconds)…", "info");
        try {
          const { data } = await generateVideo(prompt, { format: videoFormat, duration: videoDuration, camera: videoCamera, reference_image: videoRefImage });
          if (data.error) {
            setBanner(data.detail ? `${data.error}: ${data.detail}` : data.error, "error");
            return;
          }
          setVideoUrl(data.video_url);
          setBanner(
            data.source === "shotstack"
              ? "✅ AI video generated successfully with Shotstack!"
              : data.source === "json2video"
              ? "✅ AI video generated successfully with JSON2Video!"
              : data.source === "demo"
              ? "ℹ️ Demo video loaded (AI APIs not available)"
              : "✅ Video ready",
            data.source === "demo" ? "info" : "success"
          );
        } catch (err) {
          if (isLimitError(err)) refreshSubscription();
          setBanner(apiErrorMessage(err), "error");
        }
        return;
      }
      if (action === "ab-test") {
        await handleAbTest();
        return;
      }
    } catch (err) {
      setBanner(apiErrorMessage(err), "error");
    } finally {
      setBusy((b) =>
        action === "generate-text"
          ? { ...b, text: false }
          : action === "generate-image"
            ? { ...b, image: false }
          : action === "generate-video"
            ? { ...b, video: false }
          : action === "post-now"
            ? { ...b, postNow: false }
          : action === "post-all"
            ? { ...b, postAll: false }
            : action === "schedule-post" && !bulkMode
              ? { ...b, schedule: false }
              : b
      );
    }
  };

  const handleCancelPost = async (postId) => {
    setBanner("Cancelling…", "info");
    try {
      await deleteScheduledPost(postId);
      setBanner("Post cancelled.", "success");
      fetchScheduledPosts();
    } catch {
      setBanner("Failed to cancel post.", "error");
    }
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40";

  const bannerClass =
    msgTone === "error"
      ? "border-red-500/40 bg-red-950/50 text-red-100"
      : msgTone === "success"
        ? "border-teal-500/40 bg-teal-950/40 text-teal-50"
        : "border-white/10 bg-white/5 text-gray-200";

  const getCards = () => [
    {
      heading: "Generate text",
      description: (
        <div className="space-y-3">
          {/* Product/Reference Image Upload */}
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleReferenceImageUpload} />
            {referenceImagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black/30">
                <img src={referenceImagePreview} alt="Product" className="w-full h-28 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-2 justify-between">
                  <span className="text-[10px] text-gray-300 truncate max-w-[70%]">{referenceImageName}</span>
                  <button onClick={clearReferenceImage} className="text-[10px] text-red-400 hover:text-red-300 px-2 py-1 bg-black/60 rounded">Remove</button>
                </div>
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/30">
                  <span className="text-[9px] font-bold text-green-400">PRODUCT DETECTED</span>
                </div>
              </div>
            ) : (
              <button onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/25 transition-all flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-gray-300">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                Upload product / reference image (optional)
              </button>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder={referenceImage ? "Describe your promotion goal..." : "Topic or campaign idea"}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className={inputClass + " flex-1"}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-2">Platform (adapts tone)</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className={inputClass}
            >
              <option value="instagram">Instagram (Energetic, Visual)</option>
              <option value="linkedin">LinkedIn (Professional, Thought-Leadership)</option>
              <option value="twitter">Twitter/X (Witty, Concise)</option>
              <option value="facebook">Facebook (Warm, Community-Focused)</option>
              <option value="default">General (Platform-Agnostic)</option>
            </select>
          </div>
        </div>
      ),
      cta: busy.text ? "Working…" : "Generate text",
      hue: 291,
      ctaAction: "generate-text",
      ctaDisabled: busy.text,
      loading: busy.text,
      loadingIcon: "✍️",
      loadingText: "Writing your caption…",
      loadingSubtext: "AI is crafting a viral caption with hashtags",
    },
    {
      heading: "Generate image",
      description: generated ? (
        <div className="space-y-3 text-sm max-h-96 overflow-y-auto">
          <div className="bg-black/40 rounded-lg p-3 border border-white/10">
            <p className="text-gray-200 leading-snug whitespace-pre-line font-medium text-sm">
              {generated.caption}
            </p>
            {generated.source && (
              <p className="mt-2 pt-2 border-t border-white/10 text-[10px] text-gray-500 flex items-center gap-1.5">
                <span className="opacity-60">Generated by</span>
                <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                  ✨ {generated.source}
                </span>
              </p>
            )}
          </div>
          <div>
            <p className="text-cyan-300/90 text-xs font-semibold mb-1">Hashtags:</p>
            <p className="text-cyan-300/80 text-xs">{generated.hashtags}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs font-semibold mb-1">Visual Direction:</p>
            <p className="text-gray-500 text-xs italic bg-black/30 p-2 rounded whitespace-pre-wrap">
              {generated.image_prompt}
            </p>
          </div>
          {imageUrl ? (
            <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Generated image</p>
              <button type="button" onClick={() => handleCtaClick("generate-image")} disabled={busy.image}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[10px] text-gray-400 hover:text-white hover:border-white/25 transition-all disabled:opacity-40">
                <svg className={`w-3 h-3 ${busy.image ? "animate-spin" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                {busy.image ? "Generating…" : "Regenerate"}
              </button>
            </div>
            <img
              src={imageUrl}
              alt="Generated for your post"
              className="w-full rounded-xl border border-white/10 shadow-lg max-h-64 object-contain bg-black/40"
              onError={() =>
                setBanner(
                  "The image URL could not be displayed. Try generating again or set OPENAI_API_KEY.",
                  "error"
                )
              }
            />
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">
          Generate text first — we will match the image to your caption and topic.
        </p>
      ),
      extra: (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          {referenceImage ? (
            <>
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1">Generation Mode</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setImageMode("promo")}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold transition-all ${imageMode === "promo" ? "bg-purple-600/20 border border-purple-500/50 text-purple-300" : "bg-white/[0.03] border border-white/10 text-gray-500 hover:border-white/20"}`}>
                  🎨 Promo Poster
                </button>
                <button type="button" onClick={() => setImageMode("product")}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold transition-all ${imageMode === "product" ? "bg-cyan-600/20 border border-cyan-500/50 text-cyan-300" : "bg-white/[0.03] border border-white/10 text-gray-500 hover:border-white/20"}`}>
                  📸 Product Photo
                </button>
              </div>
              <p className="text-[10px] text-gray-600">
                {imageMode === "promo" ? "Full ad poster with text, CTA & branding" : "Clean product shot with studio lighting"}
              </p>
              <button type="button" onClick={() => setShowPromoTemplate(true)}
                className="w-full py-1.5 rounded-lg bg-white/[0.02] border border-white/8 text-[10px] text-gray-600 hover:text-gray-400 transition-all">
                Or build manually in template editor →
              </button>
            </>
          ) : (
            <p className="text-xs text-gray-500">Output: 2K quality • Photorealistic</p>
          )}
        </div>
      ),
      cta: busy.image ? "Generating…" : (referenceImage ? (imageMode === "promo" ? "Generate Promo Poster" : "Generate Product Photo") : "Generate image"),
      hue: 338,
      ctaAction: "generate-image",
      ctaDisabled: busy.image,
      loading: busy.image,
      loadingIcon: "🎨",
      loadingText: referenceImage ? (imageMode === "promo" ? "Creating your promo poster…" : "Creating product photo…") : "Generating your image…",
      loadingSubtext: "Seedream 4.5 is working its magic — up to 30 seconds",
    },
    {
      heading: "Generate video",
      description: (() => {
        const videoUsage = subscription?.usage?.videos;
        const isAdmin = subscription?.tier === "admin" || subscription?.tier === "enterprise";
        const isUnlimited = isAdmin || (videoUsage && videoUsage.limit >= 999999);
        const isLocked = !isUnlimited && videoUsage && videoUsage.limit === 0;
        const isExhausted = !isUnlimited && videoUsage && videoUsage.limit > 0 && videoUsage.used >= videoUsage.limit;
        if (isLocked) return (
          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-950/60 to-pink-950/40 border border-purple-500/30 text-center space-y-2">
              <div className="text-3xl">🎬</div>
              <p className="text-white font-bold text-sm">Video generation — Professional+</p>
              <p className="text-gray-400 text-xs">Unlock AI video creation by upgrading your plan. Get 5 videos/month on Professional.</p>
              <a href="#pricing" onClick={e => { e.preventDefault(); window.dispatchEvent(new CustomEvent("nav:pricing")); }}
                className="inline-block mt-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold hover:opacity-90 transition-all">
                Upgrade to unlock videos
              </a>
            </div>
          </div>
        );
        if (isExhausted) return (
          <UpgradePrompt resource="videos" used={videoUsage.used} limit={videoUsage.limit} tier={subscription.tier} />
        );
        return generated ? (
          <div className="space-y-3 text-sm max-h-96 overflow-y-auto">
            <div className="bg-black/40 rounded-lg p-3 border border-white/10">
              <p className="text-gray-200 leading-snug whitespace-pre-line font-medium text-sm">
                {generated.caption}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-xs font-semibold mb-1">Video Prompt:</p>
              <p className="text-gray-500 text-xs italic bg-black/30 p-2 rounded whitespace-pre-wrap">
                {generated.video_prompt || generated.image_prompt}
              </p>
            </div>
            {videoUrl ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Generated video</p>
                  <button type="button" onClick={() => handleCtaClick("generate-video")} disabled={busy.video}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[10px] text-gray-400 hover:text-white hover:border-white/25 transition-all disabled:opacity-40">
                    <svg className={`w-3 h-3 ${busy.video ? "animate-spin" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                    {busy.video ? "Generating…" : "Regenerate"}
                  </button>
                </div>
                <video
                  src={videoUrl}
                  controls
                  className="w-full rounded-xl border border-white/10 shadow-lg max-h-64 object-contain bg-black/40"
                  preload="metadata"
                />
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-300 text-xs font-medium">🎬 AI-powered video generation via Seedance 1.0 Pro</p>
                  <p className="text-blue-200/70 text-xs mt-1">ByteDance AI • Cinematic quality • 30-60 second generation</p>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Generate text first — we will create a video based on your caption and topic.</p>
        );
      })(),
      extra: (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-500">Duration:</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setVideoDuration("5")}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${videoDuration === "5" ? "bg-purple-600 text-white" : "bg-white/5 text-gray-400 border border-white/10 hover:border-purple-500/50"}`}>
                5s
              </button>
              <button type="button" onClick={() => setVideoDuration("10")}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${videoDuration === "10" ? "bg-purple-600 text-white" : "bg-white/5 text-gray-400 border border-white/10 hover:border-purple-500/50"}`}>
                10s
              </button>
            </div>
          </div>
          <p className="text-[10px] text-gray-600">1080p • Dynamic camera • Powered by Seedance AI</p>
        </div>
      ),
      cta: (() => {
        const videoUsage = subscription?.usage?.videos;
        const isAdmin = subscription?.tier === "admin" || subscription?.tier === "enterprise";
        const isUnlimited = isAdmin || (videoUsage && videoUsage.limit >= 999999);
        if (!isUnlimited && videoUsage?.limit === 0) return "Upgrade to unlock";
        if (!isUnlimited && videoUsage && videoUsage.used >= videoUsage.limit) return "Limit reached";
        return busy.video ? "Generating…" : "Generate video";
      })(),
      hue: 280,
      ctaAction: "generate-video",
      ctaDisabled: (() => {
        if (busy.video) return true;
        const isAdmin = subscription?.tier === "admin" || subscription?.tier === "enterprise";
        const vl = subscription?.usage?.videos;
        if (isAdmin || (vl && vl.limit >= 999999)) return false;
        if (vl?.limit === 0) return true;
        if (vl && vl.used >= vl.limit) return true;
        return false;
      })(),
      loading: busy.video,
      loadingIcon: "🎬",
      loadingText: referenceImage ? "Creating your product video…" : "Generating your video…",
      loadingSubtext: "Seedance 1.5 Pro is rendering — 30-90 seconds",
    },
    {
      heading: "A/B Test Posts",
      description: abTests.length > 0 ? (
        <div className="space-y-4">
          {abTests.map((test, i) => (
            <div key={i} className="bg-black/40 p-4 rounded-lg">
              <h4 className="text-white font-semibold">Variant {test.variant}</h4>
              <p className="text-gray-300">{test.caption}</p>
              <p className="text-cyan-300 text-sm">Hashtags: {test.hashtags}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">
          Generate multiple caption variants for testing performance.
        </p>
      ),
      cta: busy.text ? "Generating…" : "A/B Test",
      hue: 120,
      ctaAction: "ab-test",
      ctaDisabled: busy.text,
    },
    {
      heading: "Post Now",
      description: (
        <div className="space-y-3">
          <label className="block text-xs text-gray-400">Post as account</label>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className={inputClass}
          >
            {accounts.length === 0 ? (
              <option value="">Connect an account first</option>
            ) : (
              accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.platform} — {a.handle}
                  {a.page_id ? ` (page ${a.page_id})` : ""}
                </option>
              ))
            )}
          </select>
          {generated && (
            <div className="bg-black/40 rounded-lg p-3 border border-white/10">
              <p className="text-gray-200 text-xs line-clamp-3">{generated.caption}</p>
            </div>
          )}
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              className="w-full rounded-xl border border-white/10 opacity-90 max-h-32 object-contain"
            />
          )}
          <p className="text-xs text-gray-500">Publishes immediately to your selected account.</p>
        </div>
      ),
      cta: busy.postNow ? "Posting…" : "Post Now",
      hue: 130,
      ctaAction: "post-now",
      ctaDisabled: busy.postNow,
    },
    {
      heading: "Post to All",
      description: (
        <div className="space-y-3">
          {generated ? (
            <div className="bg-black/40 rounded-lg p-3 border border-white/10">
              <p className="text-gray-200 text-xs line-clamp-2">{generated.caption}</p>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Generate text first — then post to every connected platform at once.</p>
          )}
          {postAllResults && (
            <div className="space-y-1">
              {postAllResults.map((r, i) => (
                <div key={i} className={`flex items-center gap-2 text-xs ${r.status === "posted" ? "text-green-400" : "text-red-400"}`}>
                  <span>{r.status === "posted" ? "✓" : "✗"}</span>
                  <span>{r.platform} — {r.handle || r.status}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500">Publishes to every connected account with one click.</p>
        </div>
      ),
      cta: busy.postAll ? "Posting…" : "Post to All Platforms",
      hue: 45,
      ctaAction: "post-all",
      ctaDisabled: busy.postAll,
    },
    {
      heading: "Schedule post",
      description: (
        <div className="space-y-3">
          <label className="block text-xs text-gray-400">Post as account</label>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className={inputClass}
          >
            {accounts.length === 0 ? (
              <option value="">Connect an account first</option>
            ) : (
              accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.platform} — {a.handle}
                  {a.page_id ? ` (page ${a.page_id})` : ""}
                </option>
              ))
            )}
          </select>
          <input
            type="datetime-local"
            value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
            className={inputClass}
          />
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              className="w-full rounded-xl border border-white/10 opacity-90 max-h-48 object-contain"
            />
          ) : null}
        </div>
      ),
      cta: busy.schedule ? "Scheduling…" : bulkMode ? "Add to Bulk" : "Schedule",
      hue: 165,
      ctaAction: "schedule-post",
      ctaDisabled: busy.schedule,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Promo Template Modal */}
      {showPromoTemplate && (
        <PromoTemplateGenerator
          productImage={referenceImagePreview}
          productImageB64={referenceImage}
          productName={topic || "NEW ARRIVAL"}
          onGenerated={(dataUrl) => { setImageUrl(dataUrl); setShowPromoTemplate(false); setBanner("✅ Template image ready!", "success"); }}
          onClose={() => setShowPromoTemplate(false)}
        />
      )}

      {/* Page Title */}
      <div className="text-center py-4">
        <h1 className="text-3xl sm:text-4xl font-black text-white">Social Media</h1>
        <p className="text-gray-400 text-sm mt-1">Draft copy, generate high-quality visuals, and schedule to connected accounts</p>
      </div>

      {msg ? (
        <p role="status" className={`text-center p-3 rounded-xl border text-sm ${bannerClass}`}>
          {msg}
        </p>
      ) : null}

      {/* ── Usage bar ── */}
      {subscription && subscription.usage && (
        <div className="p-4 rounded-xl bg-gray-900/60 border border-white/10 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">
              Monthly Usage — <span className="capitalize text-purple-400">{subscription.tier}</span> Plan
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <UsageBadge label="Posts"     used={subscription.usage.posts.used}           limit={subscription.usage.posts.limit} />
            <UsageBadge label="Images"    used={subscription.usage.images.used}          limit={subscription.usage.images.limit} />
            <UsageBadge label="Videos"    used={subscription.usage.videos.used}          limit={subscription.usage.videos.limit} />
            <UsageBadge label="Accounts"  used={subscription.usage.social_accounts.used} limit={subscription.usage.social_accounts.limit} />
            <UsageBadge label="Scheduled" used={subscription.usage.scheduled_posts.used} limit={subscription.usage.scheduled_posts.limit} />
          </div>
        </div>
      )}

      {/* ── Connect Social Accounts panel (TOP — primary action) ── */}
      <div className="rounded-2xl border border-white/10 bg-[#0d1117] shadow-2xl shadow-black/40 overflow-hidden">
        {/* Header row with connected accounts */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 bg-black/30">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔗</span>
            <h3 className="text-white font-bold text-base">Connected Accounts</h3>
          </div>
          {accounts.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {accounts.map(a => (
                <span key={a.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10 text-xs text-gray-200">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                  {a.platform} · {a.handle}
                </span>
              ))}
            </div>
          )}
          {accounts.length === 0 && (
            <span className="text-xs text-gray-500">No accounts connected yet</span>
          )}
        </div>

        {/* OAuth buttons grid */}
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <button type="button" onClick={handleFacebookConnect}
            className="group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-[#1877F2]/30 hover:border-[#1877F2]/70 transition-all duration-200 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(145deg, rgba(24,119,242,0.15) 0%, rgba(24,119,242,0.05) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.385C19.612 22.954 24 17.99 24 12" fill="#1877F2"/></svg>
            <span className="text-xs font-semibold text-white">Facebook</span>
            <span className="text-[10px] text-[#1877F2]">+ Instagram</span>
          </button>

          <button type="button" onClick={handleTwitterConnect}
            className="group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-white/15 hover:border-white/40 transition-all duration-200 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(145deg, rgba(0,0,0,0.8) 0%, rgba(20,20,20,0.6) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            <span className="text-xs font-semibold text-white">Twitter / X</span>
            <span className="text-[10px] text-gray-500">OAuth 2.0</span>
          </button>

          <button type="button" onClick={handleLinkedInConnect}
            className="group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-[#0A66C2]/30 hover:border-[#0A66C2]/70 transition-all duration-200 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(145deg, rgba(10,102,194,0.15) 0%, rgba(10,102,194,0.05) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/></svg>
            <span className="text-xs font-semibold text-white">LinkedIn</span>
            <span className="text-[10px] text-[#0A66C2]">OAuth 2.0</span>
          </button>

          <button type="button" onClick={handleTikTokConnect}
            className="group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-[#25f4ee]/30 opacity-60 hover:opacity-80 transition-all duration-200 cursor-not-allowed"
            style={{ background: 'linear-gradient(145deg, rgba(37,244,238,0.10) 0%, rgba(254,44,85,0.10) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.3 0 .59.04.86.11v-3.5a6.37 6.37 0 00-.86-.06A6.33 6.33 0 003.16 15.7a6.33 6.33 0 006.33 6.33 6.33 6.33 0 006.33-6.33V9.4a8.16 8.16 0 004.77 1.52V7.47a4.85 4.85 0 01-1-.78z" fill="#25f4ee"/><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.3 0 .59.04.86.11v-3.5a6.37 6.37 0 00-.86-.06A6.33 6.33 0 003.16 15.7a6.33 6.33 0 006.33 6.33 6.33 6.33 0 006.33-6.33V9.4a8.16 8.16 0 004.77 1.52V7.47a4.85 4.85 0 01-1-.78z" fill="#fe2c55" style={{transform: 'translate(1px, -1px)'}}/><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.3 0 .59.04.86.11v-3.5a6.37 6.37 0 00-.86-.06A6.33 6.33 0 003.16 15.7a6.33 6.33 0 006.33 6.33 6.33 6.33 0 006.33-6.33V9.4a8.16 8.16 0 004.77 1.52V7.47a4.85 4.85 0 01-1-.78z" fill="white"/></svg>
            <span className="text-xs font-semibold text-white">TikTok</span>
            <span className="text-[10px] text-amber-500/80">Coming Soon</span>
          </button>

          {/* YouTube — Coming Soon */}
          <button type="button"
            onClick={() => toast.info("YouTube integration is pending Google API verification. Expected in 2-4 weeks!")}
            className="group relative flex flex-col items-center gap-2.5 p-4 rounded-xl border border-[#FF0000]/20 opacity-60 hover:opacity-80 transition-all duration-200 cursor-not-allowed"
            style={{ background: 'linear-gradient(145deg, rgba(255,0,0,0.12) 0%, rgba(255,0,0,0.03) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" fill="#FF0000"/>
            </svg>
            <span className="text-xs font-semibold text-white">YouTube</span>
            <span className="text-[10px] text-amber-500/80">Pending Verification</span>
            <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-[8px] font-bold px-1 rounded">SOON</span>
          </button>

          <button type="button" onClick={handlePinterestConnect}
            className="group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-[#E60023]/20 opacity-60 hover:opacity-80 transition-all duration-200 cursor-not-allowed"
            style={{ background: 'linear-gradient(145deg, rgba(230,0,35,0.12) 0%, rgba(230,0,35,0.03) 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" fill="#E60023"/></svg>
            <span className="text-xs font-semibold text-white">Pinterest</span>
            <span className="text-[10px] text-amber-500/80">Coming Soon</span>
          </button>
        </div>
      </div>

      {/* ── Bulk mode toggle ── */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)} className="rounded" />
          Bulk Mode
        </label>
      </div>

      <CardSection
        heading="Social media matrix"
        subtitle="Draft copy, generate high-quality visuals, and schedule to connected accounts"
        cardData={getCards()}
        onCtaClick={handleCtaClick}
      />
      {bulkPosts.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4">Bulk Schedule Queue ({bulkPosts.length} posts)</h3>
          <div className="space-y-3 mb-4">
            {bulkPosts.map((post, index) => (
              <div key={index} className="p-4 bg-black/30 rounded-xl border border-white/10 flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-300">{post.content.substring(0, 100)}...</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(post.post_time).toLocaleString()}</p>
                </div>
                <button
                  onClick={() => setBulkPosts(prev => prev.filter((_, i) => i !== index))}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleBulkSchedule}
            disabled={busy.bulkSchedule}
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-500 disabled:opacity-50"
          >
            {busy.bulkSchedule ? "Scheduling…" : "Schedule All"}
          </button>
        </div>
      )}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
        <InfoCard title="Upcoming scheduled posts">
          {scheduledPosts.length > 0 ? (
            <ul className="space-y-4">
              {scheduledPosts.map((post) => (
                <li
                  key={post.id}
                  className="p-4 bg-black/30 rounded-xl border border-white/10 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-white">
                      {post.platform} — @{post.handle}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(post.post_time).toLocaleString()}
                    </p>
                    <p className="mt-2 text-gray-300 text-sm leading-relaxed">
                      {post.content}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCancelPost(post.id)}
                    className="shrink-0 px-3 py-2 rounded-lg bg-red-600/90 text-white text-sm font-medium hover:bg-red-500 transition-colors"
                  >
                    Cancel
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">
              No posts scheduled yet. Generate content and schedule some!
            </p>
          )}
        </InfoCard>
        <InfoCard title="Content Calendar">
          {scheduledPosts.length > 0 ? (
            (() => {
              const grouped = scheduledPosts.reduce((acc, post) => {
                const date = new Date(post.post_time).toDateString();
                if (!acc[date]) acc[date] = [];
                acc[date].push(post);
                return acc;
              }, {});
              return (
                <div>
                  {Object.entries(grouped).map(([date, posts]) => (
                    <div key={date} className="mb-4">
                      <h4 className="text-sm font-semibold text-white mb-2">{date}</h4>
                      <ul className="space-y-2">
                        {posts.map((post) => (
                          <li key={post.id} className="text-xs text-gray-400 bg-black/20 p-2 rounded">
                            {new Date(post.post_time).toLocaleTimeString()} - {post.platform}: {post.content.substring(0, 60)}...
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            <p className="text-gray-500 text-sm">
              No posts in calendar.
            </p>
          )}
        </InfoCard>
      </div>
    </div>
  );
}
